import React from 'react';

import imgControl from '../../assets/manual/desktop-control.png';
import imgControlTeam from '../../assets/manual/desktop-control-team.png';
import imgTeams from '../../assets/manual/desktop-teams.png';
import imgTeamCard from '../../assets/manual/desktop-team-card.png';
import imgPicker from '../../assets/manual/desktop-team-picker.png';
import imgMusic from '../../assets/manual/desktop-music.png';
import imgMusicOverlay from '../../assets/manual/desktop-music-overlay.png';
import imgVideo from '../../assets/manual/desktop-video.png';
import imgGoal from '../../assets/manual/desktop-goal-animation.png';
import imgSettings from '../../assets/manual/desktop-settings.png';
import imgMenuLeft from '../../assets/manual/desktop-menu-left.png';
import imgMenuRight from '../../assets/manual/desktop-menu-right.png';
import imgLiveview from '../../assets/manual/desktop-liveview.png';

// Beginner's guide to the desktop edition — written for whoever happens to be at the desk on match
// day, not for someone who already knows the product. Short sentences, one action per step, and a
// picture for anything the reader has to find on screen.
//
// The screenshots are of the German interface, which is what the clubs run. They are captured from
// the real app (see the capture script in the commit that added them), so they can be regenerated
// rather than redrawn when the UI moves.

const content = {
  de: {
    intro: {
      title: 'Für wen ist diese Anleitung?',
      text: 'Für alle, die am Spieltag am Pult sitzen — auch ohne Vorkenntnisse. Du brauchst nichts weiter als die installierte App und etwa eine halbe Stunde, um deinen Verein einmal zu hinterlegen. Danach dauert die Vorbereitung eines Spiels weniger als eine Minute.',
      hint: 'Alles, was du hier einstellst, bleibt gespeichert. Du machst es einmal — nicht vor jedem Spiel.',
    },
    sections: [
      {
        id: 'zwei-fenster',
        title: '1. Zwei Fenster verstehen',
        lead: 'Die App zeigt zwei verschiedene Bilder: eines für dich, eines für die Zuschauer.',
        steps: [
          'Das Bedienfenster gehört dir. Hier drückst du Start, zählst Tore und erfasst Strafen.',
          'Das Zuschauerfenster zieht du auf den Beamer oder den grossen Bildschirm in der Halle.',
          'Beide zeigen immer dasselbe Spiel. Was du änderst, erscheint sofort drüben.',
        ],
        image: imgControl,
        caption: 'Das Bedienfenster: links das Heimteam, rechts das Gastteam, in der Mitte Uhr und Spielabschnitt.',
        tip: 'Unten links und unten rechts sind zwei runde Knöpfe. Das sind die beiden Menüs — links alles zum Spiel, rechts alles zur Einrichtung.',
      },
      {
        id: 'teams',
        title: '2. Teams anlegen',
        lead: 'Das ist der grösste Vorteil gegenüber der Web-Version: Du hinterlegst deine Vereine einmal mit Wappen und Kader. Am Spieltag wählst du sie nur noch aus.',
        steps: [
          'Öffne unten rechts das Menü und klicke auf „Medien & Teams". Jeder Bereich dort lässt sich mit einem Klick auf seine Überschrift auf- und zuklappen.',
          'Klappe „Ligen & Teams" auf. Gib einen Ligennamen ein, zum Beispiel „NLA Damen", und klicke „Liga hinzufügen". Die Liga ist nur eine Schublade, damit du später den Überblick behältst.',
          'Klicke „Team hinzufügen" und schreibe den Vereinsnamen in das Feld. Ein neues Team ist aufgeklappt; beim nächsten Besuch erscheinen die Teams zugeklappt als Liste — ein Klick öffnet eines.',
          'Klicke „Team-Logo hochladen" und wähle das Wappen als Bilddatei aus.',
          'Klicke „+ Spieler hinzufügen" — einmal pro Spielerin. Trage links die Nummer und rechts den Namen ein.',
        ],
        image: imgTeamCard,
        caption: 'Ein fertiges Team: Name, Wappen und der Kader mit Nummern.',
        tip: 'Du musst nicht den ganzen Kader erfassen. Aber nur wer hier steht, kann später als Torschützin ausgewählt werden.',
      },
      {
        id: 'teams-liste',
        title: '3. Mehrere Teams und Ligen',
        lead: 'Wiederhole den letzten Schritt für jeden Verein, gegen den ihr spielt. Auch die Gastteams — dann erscheinen deren Wappen automatisch auf der Anzeigetafel.',
        image: imgTeams,
        caption: 'Eine Liga mit zwei zugeklappten Teams — rechts steht, wie viele Spielerinnen jedes hat. Ligen kannst du beliebig viele anlegen.',
      },
      {
        id: 'team-waehlen',
        title: '4. Ein Team ins Spiel holen',
        lead: 'Jetzt am Spieltag — das geht in Sekunden.',
        steps: [
          'Klicke im Bedienfenster über dem Teamnamen auf „Team auswählen".',
          'Suche das Team in der Liste oder tippe die ersten Buchstaben ins Suchfeld.',
          'Klicke es an. Name und Wappen erscheinen sofort auf beiden Bildschirmen.',
        ],
        image: imgPicker,
        caption: 'Die Teamauswahl. Oben das Suchfeld, darunter die Teams nach Liga gruppiert.',
        tip: 'Für ein Team, das du nur einmal brauchst, gibt es „Ohne gespeichertes Team" — dann tippst du den Namen direkt ein.',
      },
      {
        id: 'musik',
        title: '5. Musik hinterlegen',
        lead: 'Du sagst der App einmal, welche Musik sie hat — und startest sie im Spiel mit einem Knopf.',
        steps: [
          'Öffne wieder „Medien & Teams" und klappe den Bereich „Musik" auf. Er ist in „Rund ums Spiel" (die YouTube-Playlist für Pause und vor/nach dem Spiel) und „Während des Spiels" (deine MP3s) gegliedert.',
          'Lade unter „Unterbruch-Musik" ein oder mehrere MP3s hoch. Die sind für kurze Unterbrüche während des Spiels.',
          'Lade unter „Tor-Musik" die Torhymne hoch. Sie läuft zum Torjubel nach einem Heimtor.',
          'Optional: Trage bei „Pausenmusik" den Link einer YouTube-Playlist ein — für die Drittelspause und vor oder nach dem Spiel.',
          'Im Spiel: Drücke den runden Musik-Knopf über dem Menü unten rechts. Die Karte öffnet sich über dem Knopf und zeigt dieselbe Gliederung — oben die Pausenmusik, darunter Unterbruch- und Tor-Musik, zuunterst die Lautstärke. Ein Druck startet; die laufende Quelle wird rot, nennt den Titel und stoppt ihn auf denselben Druck. Derselbe runde Knopf schliesst die Karte wieder. Startet die App die Musik selbst, zeigt sich die Karte kurz von allein.',
        ],
        twoImages: [
          { src: imgMusic, caption: 'Der Musikbereich in „Medien & Teams". Mehrere Stücke pro Kategorie sind gut — die App wählt selbst aus und wiederholt sich nicht sofort.' },
          { src: imgMusicOverlay, caption: 'Die Karte im Spiel: ein Druck pro Zeile. Was nicht hinterlegt ist, steht grau da und sagt es.' },
        ],
        tip: 'Wenn die App die Musik lieber selbst starten soll: Schalte in „Medien & Teams" unter „Musik" den Schalter „Musik automatisch abspielen" ein. Dann läuft sie bei einem Unterbruch von selbst und hört auf, wenn die Uhr wieder läuft. Der Musik-Knopf bleibt trotzdem da.',
      },
      {
        id: 'videos',
        title: '6. Videos vorbereiten',
        lead: 'Videos laufen nicht einzeln, sondern in einer Sequenz — einer Liste, die von oben nach unten abgespielt wird. Typisch: Sponsorenclip, dann Einlaufvideo.',
        steps: [
          'Klappe den Bereich „Pre-Game-Video" auf und gib einen Namen für die Sequenz ein, zum Beispiel „Einlauf", und klicke „Sequenz erstellen".',
          'Lade darunter deine Videodateien hoch.',
          'Klicke bei einem Video auf „Zur Sequenz hinzufügen".',
          'Die Reihenfolge änderst du, indem du die Einträge mit der Maus verschiebst.',
        ],
        image: imgVideo,
        caption: 'Der Videobereich: oben die Sequenzen, darunter die hochgeladenen Videos.',
        tip: 'Gestartet wird die Sequenz später im rechten Menü unter „Pre-Game-Video". Das Video übernimmt den Zuschauerbildschirm und gibt ihn danach von selbst wieder frei.',
      },
      {
        id: 'torjubel',
        title: '7. Den Torjubel einstellen',
        lead: 'Ein Tor zählt nur den Punkt — die Uhr läuft weiter und es ertönt nichts. Gefeiert wird beim Unterbruch: Dann fragt die App, wer getroffen hat. Ohne eigenes Video zeichnet sie den Jubel selbst, mit deinem Vereinswappen. Gewinnt ihr zu null, feiert sie beim Schlusspfiff auch eure Torhüterin.',
        steps: [
          'Klappe in „Medien & Teams" den Bereich „Tor-Animation" auf.',
          'Trage das Wort ein, das durchs Bild laufen soll, zum Beispiel „TOR!!!".',
          'Wähle Hintergrund- und Textfarbe.',
          'Entscheide mit den zwei Schaltern, ob die Nummer und der Name der Torschützin eingeblendet werden.',
          'Schalte „Animation nach einem Spieler-Clip“ ein, wenn nach einem eigenen Jubelvideo noch die Animation mit Nummer und Name folgen soll — so weiss die Halle, wer getroffen hat.',
          'Für den Shutout-Jubel: schalte „Shutout feiern“ ein und trage das Wort ein, zum Beispiel „SHUTOUT!“. Markiere im Kader eure Torhüterinnen mit einem Klick auf das Kürzel „TH“ neben der Nummer — es wird dann blau. Ihr Video lädst du wie bei allen unter „Tor-Medien“ hoch — beim Heimtor stehen Torhüterinnen dann nicht mehr in der Auswahl.',
          'Endet das Spiel mit einem Sieg ohne Gegentor, fragt die App beim Schlusspfiff, welche Torhüterin gespielt hat. Ein Klick startet den Jubel samt Tor-Musik; „Überspringen“ lässt ihn aus.',
          'Im Spiel: Erfasse das Tor, wenn es fällt. Stoppst du danach die Uhr, erscheint die Auswahl mit dem Kader. Weisst du nicht, wer getroffen hat, drückst du „Allgemeiner Torjubel“ — Jubel und Musik ohne Namen. „Überspringen“ lässt alles aus.',
        ],
        image: imgGoal,
        caption: 'Die Vorschau unten zeigt sofort, wie es auf dem grossen Bildschirm aussieht.',
        tip: 'Wenn du für eine einzelne Spielerin ein eigenes Jubelvideo hast, lade es beim Kader unter „Tor-Medien" hoch. Dann läuft ihres statt der gezeichneten Animation.',
      },
      {
        id: 'einstellungen',
        title: '8. Einstellungen prüfen',
        lead: 'Einmal durchgehen, dann stimmt es für die ganze Saison.',
        steps: [
          'Öffne unten links das Menü und klicke auf „Einstellungen".',
          'Stelle die Spielzeit ein — bei den meisten Ligen 25 Minuten pro Abschnitt.',
          'Stelle die Pausendauer ein.',
          'Wähle unter „Anzeigetafel" eure Vereinsfarbe.',
          'Entscheide, ob der Buzzer klingen soll und ob die Teamstrafe auf dem Zuschauerbildschirm erscheint.',
        ],
        image: imgSettings,
        caption: 'Die Einstellungen. Alles wird sofort gespeichert — es gibt keinen Speichern-Knopf.',
      },
      {
        id: 'menues',
        title: '9. Die zwei Menüs am Spieltag',
        lead: 'Während des Spiels brauchst du fast nur diese beiden Knöpfe unten am Rand.',
        twoImages: [
          { src: imgMenuLeft, caption: 'Links: alles zum Spiel — Pause starten, Spielabschnitt wechseln, zurücksetzen, Einstellungen.' },
          { src: imgMenuRight, caption: 'Rechts: alles zur Einrichtung — Medien & Teams und Pre-Game-Video. Die Musik hat ihren eigenen Knopf darüber.' },
        ],
      },
      {
        id: 'spieltag',
        title: '10. Der Ablauf am Spieltag',
        lead: 'Wenn alles vorbereitet ist, sieht ein Spiel so aus:',
        numbered: [
          'App starten. Das Zuschauerfenster auf den Beamer ziehen.',
          'Heim- und Gastteam auswählen.',
          'Vor dem Spiel: Musik-Knopf unten rechts → „Pausenmusik starten" für Stimmung in der Halle, danach im rechten Menü das Einlaufvideo starten.',
          'Anpfiff: START drücken.',
          'Tor: beim richtigen Team auf + drücken und die Torschützin antippen. Jubel und Musik laufen von selbst.',
          'Strafe: „Strafe hinzufügen" beim betroffenen Team, dann die Zeit antippen und anpassen.',
          'Ende des Spielabschnitts: die Uhr stoppt selbst, die Pause startet automatisch (wenn eingeschaltet).',
        ],
        image: imgLiveview,
        caption: 'So sieht es für die Zuschauer aus.',
        tip: 'Taste „b" auf der Tastatur löst das Horn aus — schneller, als zur Maus zu greifen.',
      },
      {
        id: 'sichern',
        title: '11. Alles sichern und übertragen',
        lead: 'Damit die Arbeit nicht an einem Rechner klebt.',
        steps: [
          'In „Medien & Teams" ganz oben auf „Exportieren" klicken. Du bekommst eine Datei mit allem: Teams, Wappen, Musik, Videos, Einstellungen.',
          'Auf dem zweiten Rechner dieselbe Stelle öffnen und „Importieren" wählen.',
        ],
        tip: 'Mach das auch als Sicherung, bevor du grössere Änderungen machst.',
      },
      {
        id: 'lizenz',
        title: '12. Testphase und Lizenz',
        lead: 'Die Desktop-Edition läuft 30 Tage vollständig, ohne Konto und ohne Zahlungsdaten.',
        steps: [
          'Oben rechts siehst du, wie viele Tage noch übrig sind.',
          'Wenn du eine Lizenz gekauft hast: oben rechts klicken, den Token einfügen und „Aktivieren" drücken.',
          'Ist die Testphase abgelaufen, kannst du den Token auf dem Sperrbildschirm eingeben.',
        ],
      },
    ],
  },
  en: {
    intro: {
      title: 'Who is this guide for?',
      text: 'For whoever sits at the desk on match day — no prior knowledge needed. All you need is the installed app and about half an hour to set your club up once. After that, preparing a match takes less than a minute.',
      hint: 'Everything you set here is saved. You do it once, not before every match.',
    },
    sections: [
      {
        id: 'zwei-fenster',
        title: '1. Understanding the two windows',
        lead: 'The app shows two different pictures: one for you, one for the audience.',
        steps: [
          'The control window is yours. This is where you press start, count goals and enter penalties.',
          'The audience window goes on the projector or the big screen in the hall.',
          'Both always show the same match. What you change appears there immediately.',
        ],
        image: imgControl,
        caption: 'The control window: home team on the left, away on the right, clock and period in the middle.',
        tip: 'There are two round buttons at the bottom left and bottom right. Those are the two menus — left is the match, right is the setup.',
      },
      {
        id: 'teams',
        title: '2. Setting up teams',
        lead: 'This is the biggest advantage over the web edition: you enter your clubs once, with crest and roster. On match day you only pick them.',
        steps: [
          'Open the menu at the bottom right and click “Medien & Teams” (Media & Teams). Each area there opens and closes with a click on its heading.',
          'Open “Leagues & teams”. Type a league name, for example “NLA Women”, and click Add league. The league is just a drawer to keep things tidy.',
          'Click Add team and write the club name in the field. A new team is open; on your next visit the teams show collapsed as a list — one click opens one.',
          'Click Upload team logo and choose the crest as an image file.',
          'Click + Add player, once per player. Enter the number on the left and the name on the right.',
        ],
        image: imgTeamCard,
        caption: 'A finished team: name, crest and the roster with numbers.',
        tip: 'You do not have to enter the whole roster. But only players listed here can later be picked as the scorer.',
      },
      {
        id: 'teams-liste',
        title: '3. More teams and leagues',
        lead: 'Repeat the last step for every club you play against — the away teams too, so their crest appears on the board by itself.',
        image: imgTeams,
        caption: 'One league with two collapsed teams — the number on the right is the size of each roster. You can create as many leagues as you like.',
      },
      {
        id: 'team-waehlen',
        title: '4. Bringing a team into the match',
        lead: 'This is the match-day part, and it takes seconds.',
        steps: [
          'In the control window, click “Team auswählen” (select team) above the team name.',
          'Find the team in the list, or type the first letters into the search box.',
          'Click it. Name and crest appear on both screens at once.',
        ],
        image: imgPicker,
        caption: 'The team picker. Search box at the top, teams grouped by league below.',
        tip: 'For a team you need only once there is an ad-hoc option — then you type the name directly.',
      },
      {
        id: 'musik',
        title: '5. Adding music',
        lead: 'You tell the app once which music it has — and start it during the match with one button.',
        steps: [
          'Open Media & Teams again and open the music area. It is split into “around the match” (the YouTube playlist for the break and before/after) and “during the match” (your MP3s).',
          'Upload one or more MP3s under stoppage music. They are for short interruptions during play.',
          'Upload your goal anthem under goal music. It plays with the celebration after a home goal.',
          'Optional: paste a YouTube playlist link under break music — for the period break and before or after the match.',
          'During the match: press the round music button above the menu at the bottom right. The card opens above the button and shows the same split — break music at the top, stoppage and goal music below, the volume at the bottom. One press starts; the running source turns red, names the track and stops it on the same press. The same round button closes the card again. When the app starts music by itself, the card shows itself briefly.',
        ],
        twoImages: [
          { src: imgMusic, caption: 'The music section in Media & Teams. Several tracks per category is good — the app picks for you and does not repeat immediately.' },
          { src: imgMusicOverlay, caption: 'The card during the match: one press per row. Anything you have not loaded is greyed out and says so.' },
        ],
        tip: 'If you would rather the app started the music itself: in Media & Teams under Music, switch “play music automatically” on. It then plays on a stoppage and stops when the clock runs again. The music button stays where it is.',
      },
      {
        id: 'videos',
        title: '6. Preparing videos',
        lead: 'Videos do not run one by one but in a sequence — a list played from top to bottom. Typically: sponsor clip, then the entrance video.',
        steps: [
          'Open the pre-game video area and type a name for the sequence, for example “Entrance”, and click Create sequence.',
          'Upload your video files below.',
          'Click Add to sequence on a video.',
          'Change the order by dragging the entries with the mouse.',
        ],
        image: imgVideo,
        caption: 'The video section: sequences at the top, uploaded videos below.',
        tip: 'You start the sequence later from the right menu under Pre-Game-Video. The video takes over the audience screen and hands it back by itself.',
      },
      {
        id: 'torjubel',
        title: '7. Setting up the goal celebration',
        lead: 'A goal only counts the point — the clock keeps running and nothing sounds. The celebration comes at the stoppage: the app then asks who scored. Without a video of your own it draws the celebration itself, with your club crest. Win without conceding and it celebrates your goalkeeper at the final whistle too.',
        steps: [
          'In Media & Teams, open the goal animation area.',
          'Enter the word that sweeps across the screen, for example “TOR!!!”.',
          'Choose the background and text colour.',
          'Use the two switches to decide whether the scorer’s number and name are shown.',
          'Turn on “Animation after a player clip” if the animation with number and name should follow a player’s own celebration video — so the hall knows who scored.',
          'For the shutout celebration: turn on “Celebrate a shutout” and enter the word, for example “SHUTOUT!”. In the roster, mark your goalkeepers by clicking the small “TH” tag next to the number — it turns blue. Upload their video under goal media like everyone else’s — goalkeepers then no longer appear in the picker when a home goal is scored.',
          'When the match ends in a win without conceding, the app asks at the final whistle which goalkeeper played. One click starts the celebration with the goal music; Skip leaves it out.',
          'During the match: record the goal when it happens. Stop the clock afterwards and the picker appears with the roster. If you do not know who scored, press “General celebration” — celebration and music without a name. Skip leaves everything out.',
        ],
        image: imgGoal,
        caption: 'The preview below shows immediately what the big screen will do.',
        tip: 'If you have a celebration video for one player, upload it in the roster under goal media. Then hers plays instead of the drawn animation.',
      },
      {
        id: 'einstellungen',
        title: '8. Checking the settings',
        lead: 'Go through this once and it is right for the season.',
        steps: [
          'Open the menu at the bottom left and click Settings.',
          'Set the period length — 25 minutes in most leagues.',
          'Set the break length.',
          'Pick your club colour under Scoreboard.',
          'Decide whether the buzzer sounds and whether the team penalty shows on the audience screen.',
        ],
        image: imgSettings,
        caption: 'The settings. Everything saves immediately — there is no save button.',
      },
      {
        id: 'menues',
        title: '9. The two menus on match day',
        lead: 'During the match you will barely need anything but these two buttons at the bottom.',
        twoImages: [
          { src: imgMenuLeft, caption: 'Left: the match — start a break, change period, reset, settings.' },
          { src: imgMenuRight, caption: 'Right: the setup — Media & Teams and the pre-game video. Music has its own button above it.' },
        ],
      },
      {
        id: 'spieltag',
        title: '10. How a match day runs',
        lead: 'With everything prepared, a match looks like this:',
        numbered: [
          'Start the app. Drag the audience window onto the projector.',
          'Pick the home and away teams.',
          'Before the match: music button at the bottom right → start the break music for atmosphere, then start the entrance video from the right menu.',
          'Kick-off: press START.',
          'Goal: press + on the right team and tap the scorer. Celebration and music run by themselves.',
          'Penalty: “add penalty” on the team concerned, then tap the time to adjust it.',
          'End of the period: the clock stops by itself and the break starts automatically (if switched on).',
        ],
        image: imgLiveview,
        caption: 'This is what the audience sees.',
        tip: 'The “b” key on the keyboard sounds the horn — quicker than reaching for the mouse.',
      },
      {
        id: 'sichern',
        title: '11. Backing everything up and moving it',
        lead: 'So the work does not stick to one machine.',
        steps: [
          'At the top of Media & Teams, click Export. You get one file with everything: teams, crests, music, videos, settings.',
          'On the second machine, open the same place and choose Import.',
        ],
        tip: 'Do this as a backup before making bigger changes, too.',
      },
      {
        id: 'lizenz',
        title: '12. Trial and licence',
        lead: 'The desktop edition runs for 30 days in full, without an account and without payment details.',
        steps: [
          'The top right corner shows how many days are left.',
          'Once you have bought a licence: click the badge at the top right, paste the token and press Activate.',
          'If the trial has already run out, you can enter the token on the block screen.',
        ],
      },
    ],
  },
};

function Figure({ src, caption }) {
  return (
    <figure className="mt-5">
      <img src={src} alt={caption} loading="lazy" className="w-full rounded-lg border border-gray-200 shadow-sm" />
      <figcaption className="mt-2 text-sm text-gray-500">{caption}</figcaption>
    </figure>
  );
}

export default function DesktopManual({ lang = 'de' }) {
  const c = content[lang] || content.de;

  return (
    <>
      <section className="mb-10 rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h2 className="mb-2 text-2xl font-semibold text-blue-900">{c.intro.title}</h2>
        <p className="text-blue-900">{c.intro.text}</p>
        <p className="mt-3 text-sm font-medium text-blue-800">{c.intro.hint}</p>
      </section>

      {/* Jump list — the guide is long, and a beginner returning to step 6 should not have to scroll. */}
      <nav className="mb-12 rounded-lg border border-gray-200 p-5">
        <ol className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
          {c.sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-blue-600 hover:text-blue-800">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {c.sections.map((s) => (
        <section key={s.id} id={s.id} className="mb-12 scroll-mt-8">
          <h2 className="mb-3 text-2xl font-semibold">{s.title}</h2>
          {s.lead && <p className="mb-4 text-lg text-gray-700">{s.lead}</p>}

          {s.steps && (
            <ol className="list-decimal space-y-2 pl-6">
              {s.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}

          {s.numbered && (
            <ol className="list-decimal space-y-2 pl-6">
              {s.numbered.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}

          {s.image && <Figure src={s.image} caption={s.caption} />}

          {s.twoImages && (
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              {s.twoImages.map((im) => (
                <Figure key={im.caption} src={im.src} caption={im.caption} />
              ))}
            </div>
          )}

          {s.tip && (
            <div className="mt-5 border-l-4 border-amber-400 bg-amber-50 p-4 text-amber-900">
              <span className="font-semibold">{lang === 'de' ? 'Tipp: ' : 'Tip: '}</span>
              {s.tip}
            </div>
          )}
        </section>
      ))}
    </>
  );
}
